import { useEnvironment } from "@keycloak/keycloak-ui-shared";
import Grid from '@mui/material/Grid2';
import { useTranslation } from "react-i18next";

import { linkAccount, unLinkAccount } from "../../../api/methods";
import { LinkedAccountRepresentation } from "../../../api/representations";
import { useAccountAlerts } from "../../../utils/useAccountAlerts";
import {Environment} from "../../../environment";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import Button from "../../../components/button/Button";
import UnlinkIcon from "../../../icons/Unlink";
import LinkIcon from "../../../icons/Link";
import { TFunction } from "i18next";
import { IconMapper } from "../../../components/IconMapper/IconMapper";
import { useMenu } from "../../../providers/MenuProvider";
import { StyledListItem } from "../../../components/datalist/styles";
import { accountRowListItemStyles } from "../styles";

type AccountRowProps = {
  account: LinkedAccountRepresentation;
  isLinked?: boolean;
  refresh: () => void;
  onUnlink?: (account: LinkedAccountRepresentation) => void;
};

export const AccountRow = ({
  account,
  isLinked = false,
  refresh,
  onUnlink,
}: AccountRowProps) => {
  return isLinked
    ? <LinkedAccountRow account={account} refresh={refresh} onUnlink={onUnlink} />
    : <UnlinkedAccountRow account={account} refresh={refresh} />;
};

// Linked state component
const LinkedAccountRow = ({
  account,
  refresh,
  onUnlink,
}: { account: LinkedAccountRepresentation; refresh: () => void; onUnlink?: (account: LinkedAccountRepresentation) => void }) => {
  const { t } = useTranslation();
  const { isOpen } = useMenu();
  const theme = useTheme();
  const isDownLg = useMediaQuery(theme.breakpoints.down('lg'));

  return (
    <StyledListItem
      aria-label={t("linkedAccounts")}
      data-testid={`ext-linked-accounts/${account.providerName}`}
      id={account.providerName}
      sx={accountRowListItemStyles}
    >
      <Grid container alignItems="center" width="100%">
        <Grid>
          <IconMapper icon={account.providerName} folderName="providers" />
        </Grid>
        <Grid size="grow" sx={{ marginLeft: { xs: '12px', md: '25px' } }}>
          <Grid container sx={{
            flexDirection: { xs: 'column', md: (isOpen && isDownLg) ? 'column' : 'row' },
            alignItems: { xs: 'flex-start', md: (isOpen && isDownLg) ? 'flex-start' : 'center' }
          }}>
            <Grid sx={{ minWidth: '160px' }}>
              {account.displayName}
            </Grid>
            {account?.linkedUsername && (
              <Grid size="grow" container sx={{
                flexDirection: { xs: 'column', md: (isOpen && isDownLg) ? 'column' : 'row' },
                alignItems: { xs: 'flex-start', md: (isOpen && isDownLg) ? 'flex-start' : 'center' }
              }}>
                <Grid size="grow">
                  <Typography color="text.secondary">{account.linkedUsername}</Typography>
                </Grid>
                <Grid sx={{ marginTop: { xs: '12px', md: (isOpen && isDownLg) ? '12px' : 0 } }}>
                  <RowAction
                    t={t}
                    isLinked={true}
                    account={account}
                    unLink={onUnlink}
                  />
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </StyledListItem>
  );
};

// Unlinked state component
const UnlinkedAccountRow = ({
  account,
  refresh,
}: { account: LinkedAccountRepresentation; refresh: () => void }) => {
  const { t } = useTranslation();
  const context = useEnvironment<Environment>();
  const { addError } = useAccountAlerts();

  const link = async (account: LinkedAccountRepresentation) => {
    try {
      const { accountLinkUri } = await linkAccount(context, account);
      location.href = accountLinkUri;
    } catch (error) {
      addError("linkError", error);
    }
  };

  return (
    <StyledListItem
      aria-label={t("linkedAccounts")}
      data-testid={`ext-linked-accounts/${account.providerName}`}
      id={account.providerName}
      sx={accountRowListItemStyles}
    >
      <Grid container alignItems="center" width="100%">
        <Grid>
          <IconMapper icon={account.providerName} folderName="providers" />
        </Grid>
        <Grid size="grow" sx={{ marginLeft: { xs: '12px', md: '25px' } }}>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid sx={{ minWidth: '160px' }}>
              {account.displayName}
            </Grid>
            <Grid sx={{ marginTop: { xs: '12px', md: 0 } }}>
              <RowAction t={t} isLinked={false} link={link} account={account} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </StyledListItem>
  );
};

interface RowActionProps {
  t: TFunction,
  isLinked: boolean;
  account: LinkedAccountRepresentation;
  unLink?: (acc: LinkedAccountRepresentation) => void;
  link?: (acc: LinkedAccountRepresentation) => void;
}

const RowAction = ({t, isLinked, account, unLink = () => {}, link = () => {}}: RowActionProps) => {
  return (
    <Box
      aria-labelledby={t("link")}
      aria-label={t("unLink")}
      id="setPasswordAction"
    >
      {isLinked && account.linkingAllowed && (
        <Button
          id={`${account.providerAlias}-idp-unlink`}
          variant="outlined"
          onClick={() => unLink(account)}
          sx={{ fontWeight: 600 }}
          size="small"
        >
          <UnlinkIcon sx={{ fontSize: '16px', marginRight: '10px' }} />
          {t("unLink")}
        </Button>
      )}
      {!isLinked && (
        <Button
          id={`${account.providerAlias}-idp-link`}
          onClick={() => link(account)}
          variant="contained"
          sx={{ fontWeight: 600 }}
          size="small"
        >
          <LinkIcon sx={{ fontSize: '16px', marginRight: '10px' }} />
          {t("link")}
        </Button>
      )}
    </Box>
  );
}
